// @flow
import React, {useEffect} from 'react';
import {useNavigate, useParams} from 'react-router-dom';

import {BridgeEventSources, Theme} from '@wellstone-solutions/common';
import {Select, Typography, Stack, Box} from '@wellstone-solutions/web/';
import {useEventSource} from 'hooks/useEventSource';
import {useStores} from 'hooks/useStores';
import {ENTITY_LEVEL, AccessControl} from 'modules/rbac';
import {NewAnnouncement, PastAnnouncements} from 'modules/announcements';

const ALL_MEMBERS_VALUE = -1;

export const AnnouncementsPage = () => {
  useEventSource(BridgeEventSources.ANNOUNCEMENTS_PAGE);

  const {group_id} = useParams();
  const {appUIStore, groupStore, RBACStore} = useStores();
  const navigate = useNavigate();

  const canSendToAllMembers = RBACStore.hasAccess(
    AccessControl.announcements.sendToAllMembers,
  );
  const canAccessAllGroups = RBACStore.hasEntityLevelAccess(ENTITY_LEVEL.all);

  const groups = canAccessAllGroups ? groupStore.groups : groupStore.myGroups;
  const selectedGroup = groups.find((g) => g.id === group_id)?.name;
  const groupName =
    canSendToAllMembers && !selectedGroup ? 'All Members' : selectedGroup;

  useEffect(() => {
    appUIStore.setBreadcrumbs([
      {
        name: 'Announcements',
        url: '/announcements',
        active: true,
      },
    ]);
  }, [appUIStore]);

  const handleChange = (event) => {
    if (event.target.value !== ALL_MEMBERS_VALUE) {
      navigate(`/announcements/${event.target.value}`);
      return;
    }

    navigate('/announcements');
  };

  const groupNameItems = [
    {label: 'All Members', value: ALL_MEMBERS_VALUE},
    ...groups.map((group) => ({
      label: group.name,
      value: group.id,
    })),
  ];

  return (
    <Box sx={styles.root}>
      <Stack
        direction={{xs: 'column', sm: 'row'}}
        alignItems="center"
        spacing={2}>
        <Typography style={styles.heading} variant="h5">
          Announcements for
        </Typography>
        <Select
          value={group_id || ALL_MEMBERS_VALUE}
          onChange={handleChange}
          items={groupNameItems}
          canUnselect={false}
          containerProps={{
            fullWidth: true,
            sx: {
              maxWidth: 400,
            },
          }}
          MenuProps={{
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'left',
            },
            transformOrigin: {
              vertical: 'top',
              horizontal: 'left',
            },
            slotProps: {
              paper: {
                sx: {
                  maxHeight: 500,
                },
              },
            },
          }}
        />
      </Stack>

      <Stack
        direction={{xs: 'column', xl: 'row'}}
        spacing={2}
        sx={styles.content}>
        <Box
          sx={{
            flex: {xs: '1 1 auto', md: '2 1 auto'},
          }}>
          <NewAnnouncement groupId={group_id} groupName={groupName} />
        </Box>
        <Box
          sx={{
            flex: '1 1 auto',
          }}>
          <PastAnnouncements groupId={group_id} />
        </Box>
      </Stack>
    </Box>
  );
};

const styles = {
  root: {
    padding: {xs: '.5rem', sm: '1rem'},
  },
  heading: {
    color: Theme.colorPalette.dark,
  },
  content: {
    marginTop: '1rem',
  },
};
